/**
 * Change Issue Status Modal
 *
 * Modal for changing the status of a container issue.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XCircle } from '@untitledui/icons';
import { Modal, ModalOverlay, Dialog } from '@/components/application/modals/modal';
import { Button } from '@/components/base/buttons/button';
import { Select } from '@/components/base/select/select';
import { TextArea } from '@/components/base/textarea/textarea';
import { toast } from '@/lib/toast-service';
import {
    useUpdateIssueStatus,
    RESOLUTION_STATUS_OPTIONS,
    type ContainerIssue,
} from '@/hooks/api/use-container-issues';

interface ChangeIssueStatusModalProps {
    isOpen: boolean;
    onClose: () => void;
    issue: ContainerIssue;
    containerId: string;
    containerNumber: string;
}

export function ChangeIssueStatusModal({
    isOpen,
    onClose,
    issue,
    containerId,
    containerNumber: _containerNumber,
}: ChangeIssueStatusModalProps) {
    const { t } = useTranslation(['pages:containers', 'common']);
    const [newStatus, setNewStatus] = useState(issue.resolution_status);
    const [reason, setReason] = useState('');

    const updateStatusMutation = useUpdateIssueStatus();

    const handleSubmit = async () => {
        if (newStatus === issue.resolution_status) {
            toast.info(t('detail.issues.statusUnchanged', 'Status is the same, no changes made'));
            onClose();
            return;
        }

        try {
            await updateStatusMutation.mutateAsync({
                issueId: issue.issue_id,
                containerId,
                data: {
                    resolution_status: newStatus,
                    status_change_reason: reason.trim() || undefined,
                },
            });
            toast.success(t('detail.issues.statusUpdated', 'Issue status updated successfully'));
            onClose();
        } catch (error) {
            console.error('Failed to update issue status:', error);
            toast.error(t('detail.issues.statusUpdateError', 'Failed to update issue status'));
        }
    };

    const handleClose = () => {
        setNewStatus(issue.resolution_status);
        setReason('');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <ModalOverlay isOpen={isOpen} onOpenChange={(open) => !open && handleClose()}>
            <Modal>
                <Dialog>
                    <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-lg w-full">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h2 className="text-xl font-semibold text-primary">
                                    {t('detail.issues.changeStatus', 'Change Issue Status')}
                                </h2>
                                <p className="text-sm text-tertiary mt-1 truncate max-w-[350px]">
                                    {issue.title}
                                </p>
                            </div>
                            <button
                                onClick={handleClose}
                                className="text-tertiary hover:text-primary"
                            >
                                <XCircle className="size-5" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="space-y-4">
                            <Select
                                label={`${t('detail.issues.newStatus', 'New Status')} *`}
                                items={RESOLUTION_STATUS_OPTIONS.map(opt => ({ id: opt.value, label: opt.label }))}
                                selectedKey={newStatus}
                                onSelectionChange={(key) => setNewStatus(key as string)}
                                size="md"
                            >
                                {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                            </Select>

                            <div>
                                <label className="text-sm font-medium text-primary mb-2 block">
                                    {t('detail.issues.reason', 'Reason')}
                                    <span className="text-tertiary font-normal ml-1">
                                        ({t('common:optional', 'optional')})
                                    </span>
                                </label>
                                <TextArea
                                    value={reason}
                                    onChange={setReason}
                                    placeholder={t(
                                        'detail.issues.reasonPlaceholder',
                                        'Explain why you are changing the status...'
                                    )}
                                    rows={3}
                                />
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex justify-end gap-3 mt-6">
                            <Button color="secondary" onClick={handleClose}>
                                {t('common:cancel', 'Cancel')}
                            </Button>
                            <Button
                                color="primary"
                                onClick={handleSubmit}
                                isLoading={updateStatusMutation.isPending}
                            >
                                {t('detail.issues.saveStatus', 'Save')}
                            </Button>
                        </div>
                    </div>
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
}
